export function backInStockPayload(email, variantId) {
  return {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        profile: { data: { type: 'profile', attributes: { email } } },
        channels: ['EMAIL']
      },
      relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } }
    }
  };
}

export function requestedVariant(variants, options) {
  return variants.find(variant => !variant.available && variant.options.length === options.length &&
    variant.options.every((value, index) => value === options[index])) || null;
}

export function restockHeading(product, variant, templates) {
  if (!variant || product.default_only) return templates.heading;
  return product.size_only
    ? templates.size.replaceAll('{size}', variant.title)
    : templates.variant.replaceAll('{variant}', variant.title);
}

// Shopify answers the customer form with JSON errors (Accept: application/json)
// or a redirect back to the page; a redirect to /challenge means the captcha
// was not solved.
export function newsletterAccepted({ ok, url, json }) {
  return Boolean(ok) && !/\/challenge(?:[/?#]|$)/.test(url || '') && !(json && json.errors);
}

// Newsletter consent goes through Shopify's customer form so that Shopify's
// double opt-in mail and the Klaviyo sync into the welcome flow apply. Shopify
// guards that form with hCaptcha: Shopify.captcha.protect() binds it, keeps the
// form's own submit() as the "real" submit and calls it once the token is set.
// Owning that function lets the signup run as a fetch instead of navigating away
// from the product page.
export function shopifyNewsletter(form, { captcha, fetchImpl, timeoutMs = 120000 } = {}) {
  let settle = null;
  form.submit = function submitWithFetch() {
    const body = new URLSearchParams(new FormData(form));
    fetchImpl(form.action, { method: 'POST', body, headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(async response => {
        const isJson = (response.headers?.get('content-type') || '').includes('json');
        const json = isJson ? await response.json().catch(() => null) : null;
        return newsletterAccepted({ ok: response.ok, url: response.url, json });
      }, () => false)
      .then(accepted => settle?.(accepted));
  };
  let bound = null;
  const bind = () => bound ||= new Promise(resolve => {
    if (!captcha?.protect) return resolve();
    const timer = setTimeout(resolve, 15000);
    captcha.protect(form, () => { clearTimeout(timer); resolve(); });
  });
  return {
    bind,
    // Resolves false after timeoutMs (e.g. an hCaptcha challenge left open);
    // an answer that still arrives later goes to onLateAnswer.
    async send(email, onLateAnswer) {
      await bind();
      form.elements['contact[email]'].value = email;
      return new Promise(resolve => {
        let timedOut = false;
        const timer = setTimeout(() => { timedOut = true; resolve(false); }, timeoutMs);
        settle = accepted => {
          settle = null;
          if (timedOut) return onLateAnswer?.(accepted);
          clearTimeout(timer);
          resolve(accepted);
        };
        form.submit();
      });
    }
  };
}

if (typeof document !== 'undefined') {
  const initialized = new WeakSet();
  function init() {
    for (const root of document.querySelectorAll('[data-eisdieler-bis]')) {
      if (initialized.has(root)) continue;
      const sectionId = root.dataset.sectionId;
      const productForm = document.querySelector(`form[data-js-product-form][data-section-id="${CSS.escape(sectionId)}"]`);
      const form = document.querySelector(`form[data-bis-form][data-section-id="${CSS.escape(sectionId)}"]`);
      const newsletterForm = document.querySelector(`form[data-bis-newsletter-form="${CSS.escape(sectionId)}"]`);
      if (!productForm || !form || !newsletterForm) continue;
      initialized.add(root);
      // In place of the add-to-cart button: rendered after the product form,
      // the request sat below description and brand, out of sight.
      const purchaseArea = productForm.querySelector('.product-add-to-cart');
      purchaseArea?.after(root);
      // The theme reacts to change events inside the product form; ours are not variant changes.
      for (const type of ['change', 'input']) root.addEventListener(type, event => event.stopPropagation());
      newsletterForm.setAttribute('aria-hidden', 'true');
      const product = JSON.parse(root.querySelector('[data-bis-product]').textContent);
      const summary = root.querySelector('[data-bis-summary]');
      const label = root.querySelector('[data-bis-variant]');
      const status = root.querySelector('[data-bis-status]');
      const button = root.querySelector('[data-bis-submit]');
      const newsletterButton = root.querySelector('[data-bis-submit-newsletter]');
      const newsletterStatus = root.querySelector('[data-bis-newsletter-status]');
      const signup = shopifyNewsletter(newsletterForm, { captcha: window.Shopify?.captcha, fetchImpl: window.fetch.bind(window) });
      const templates = { heading: root.dataset.heading, size: root.dataset.headingSize, variant: root.dataset.headingVariant };
      const savedRestocks = new Set();
      const savedNewsletters = new Set();
      let variant = null;
      let pending = false;
      const update = () => {
        const fieldsets = [...productForm.querySelectorAll('fieldset')].filter(fieldset => fieldset.querySelector('input[name^="option"]'));
        let options = fieldsets.map(fieldset => fieldset.querySelector('input[type="radio"]:checked')?.value);
        if (!options.length) {
          const variantId = productForm.querySelector('[name="id"]')?.value;
          options = product.variants.find(item => String(item.id) === String(variantId))?.options || [];
        }
        variant = requestedVariant(product.variants, options);
        root.hidden = !variant;
        if (!variant) root.open = false;
        // A sold-out selection gets the request button instead of a disabled "Ausverkauft".
        purchaseArea?.classList.toggle('eisdieler-bis-active', Boolean(variant));
        status.textContent = '';
        newsletterStatus.textContent = '';
        summary.textContent = restockHeading(product, variant, templates);
        label.textContent = !variant ? '' : product.default_only ? product.title : `${product.title} — ${variant.title}`;
      };
      update();
      productForm.addEventListener('change', update);
      window.themeCore?.EventBus.listen(`pdp:section-${sectionId}:change-variant`, update);
      // Which button was pressed decides the newsletter consent. hCaptcha loads
      // only when someone presses the newsletter one (signup.send binds it).
      let choice = null;
      const submitWith = wantsNewsletter => {
        choice = wantsNewsletter;
        try {
          if (form.requestSubmit) form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true }));
        } finally {
          choice = null;
        }
      };
      newsletterButton.addEventListener('click', () => submitWith(true));
      button?.addEventListener('click', () => submitWith(false));
      const lock = locked => {
        pending = locked;
        if (button) button.disabled = locked;
        newsletterButton.disabled = locked;
        form.elements.email.disabled = locked;
      };
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const wantsNewsletter = choice;
        // Enter in the email field: consent needs the labelled button, so hand the choice back.
        if (wantsNewsletter === null) {
          newsletterButton.focus();
          return;
        }
        if (pending || !variant || !form.reportValidity()) return;
        const selectedId = variant.id;
        const email = form.elements.email.value.trim();
        const requestKey = JSON.stringify([email, selectedId]);
        const current = () => variant?.id === selectedId;
        lock(true);
        status.textContent = '';
        newsletterStatus.textContent = '';
        const restock = (async () => {
          if (savedRestocks.has(requestKey)) return true;
          try {
            const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(root.dataset.companyId)}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-10-15' },
              body: JSON.stringify(backInStockPayload(email, selectedId))
            });
            if (response.ok) savedRestocks.add(requestKey);
            return response.ok;
          } catch {
            return false;
          }
        })().then(saved => {
          if (current()) status.textContent = saved ? status.dataset.success : status.dataset.error;
          return saved;
        });
        const showNewsletter = sent => {
          if (sent) savedNewsletters.add(email);
          if (current()) newsletterStatus.textContent = sent ? newsletterStatus.dataset.success : newsletterStatus.dataset.error;
          return sent;
        };
        const subscribe = !wantsNewsletter ? Promise.resolve(true) :
          savedNewsletters.has(email) ? Promise.resolve(showNewsletter(true)) :
          signup.send(email, late => { if (late) showNewsletter(true); }).then(showNewsletter);
        try {
          const [restockSaved, newsletterSent] = await Promise.all([restock, subscribe]);
          if (current() && restockSaved && newsletterSent) form.elements.email.value = '';
        } finally {
          lock(false);
        }
      });
    }
  }
  if (window.themeCore?.loaded) init();
  else document.addEventListener('theme:all:loaded', init, { once: true });
  document.addEventListener('shopify:section:load', init);
}
